/**
 * User Settings Page
 * 
 * Allows users to manage their profile and preferences:
 * - Name (profile)
 * - Theme (light/dark/system)
 * - Timezone
 * - Table page size
 * - Sidebar collapsed state
 */

import { useState, useEffect } from 'react';
import { useUserPreferences, useUpdateUserPreferences, useCurrentUser, useUpdateUserProfile } from '@/hooks/usePreferences';
import { Settings, User, Mail, CheckCircle, AlertTriangle, Send, Grid, List } from 'lucide-react';
import { sendVerificationEmail } from '@/lib/auth-client';
import toast from 'react-hot-toast';
import {
  PageHeader,
  StatsGrid,
  StatCard,
  LoadingState,
  ErrorState,
} from '../../components/common';

type Theme = 'light' | 'dark' | 'system';
type ViewMode = 'grid' | 'list';

// Common IANA timezones for dropdown
const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (CET/CEST)' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (EET/EEST)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'America/Toronto', label: 'America/Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (PST/PDT)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)' },
] as const;

// Page size options for tables
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export function UserSettingsPage() {
  const { data: currentUser, isLoading: userLoading, refetch: refetchUser } = useCurrentUser();
  const { data: preferences, isLoading: prefsLoading, error, refetch: refetchPrefs, isFetching } = useUserPreferences();
  const updatePreferences = useUpdateUserPreferences();
  const updateProfile = useUpdateUserProfile();
  
  // Profile state
  const [name, setName] = useState('');
  const [hasProfileChanges, setHasProfileChanges] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  
  // Preferences state
  const [theme, setTheme] = useState<Theme>('system');
  const [timezone, setTimezone] = useState('UTC');
  const [tablePageSize, setTablePageSize] = useState(25);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [defaultViewMode, setDefaultViewMode] = useState<ViewMode>('list');
  const [hasPreferencesChanges, setHasPreferencesChanges] = useState(false);

  // Load user profile into form
  useEffect(() => {
    if (currentUser?.user) {
      setName(currentUser.user.name || '');
      setHasProfileChanges(false);
    }
  }, [currentUser]);

  // Load preferences into form
  useEffect(() => {
    if (preferences) {
      setTheme(preferences.theme as Theme);
      setTimezone(preferences.timezone);
      setTablePageSize(preferences.tablePageSize);
      setSidebarCollapsed(preferences.sidebarCollapsed);
      setDefaultViewMode((preferences.defaultViewMode as ViewMode) || 'list');
      setHasPreferencesChanges(false);
    }
  }, [preferences]);

  // Track profile changes
  useEffect(() => {
    if (currentUser?.user) {
      setHasProfileChanges(name !== (currentUser.user.name || ''));
    }
  }, [name, currentUser]);

  // Track preferences changes
  useEffect(() => {
    if (preferences) {
      const changed = 
        theme !== preferences.theme ||
        timezone !== preferences.timezone ||
        tablePageSize !== preferences.tablePageSize ||
        sidebarCollapsed !== preferences.sidebarCollapsed ||
        defaultViewMode !== (preferences.defaultViewMode || 'list');
      setHasPreferencesChanges(changed);
    }
  }, [theme, timezone, tablePageSize, sidebarCollapsed, defaultViewMode, preferences]);

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      await updateProfile.mutateAsync({ name: name.trim() });
      toast.success('Profile updated successfully');
      setHasProfileChanges(false);
      refetchUser();
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  const handleSavePreferences = async () => {
    try {
      await updatePreferences.mutateAsync({
        theme,
        timezone,
        tablePageSize,
        sidebarCollapsed,
        defaultViewMode,
      });
      toast.success('Preferences saved successfully');
      setHasPreferencesChanges(false);
    } catch (err) {
      toast.error('Failed to save preferences');
    }
  };

  const handleResetPreferences = () => {
    if (preferences) {
      setTheme(preferences.theme as Theme);
      setTimezone(preferences.timezone);
      setTablePageSize(preferences.tablePageSize);
      setSidebarCollapsed(preferences.sidebarCollapsed);
      setDefaultViewMode((preferences.defaultViewMode as ViewMode) || 'list');
      setHasPreferencesChanges(false);
    }
  };

  const handleRefresh = () => {
    refetchUser();
    refetchPrefs();
  };

  const handleResendVerification = async () => {
    if (!currentUser?.user?.email) return;
    
    setIsResendingVerification(true);
    try {
      const { error } = await sendVerificationEmail({
        email: currentUser.user.email,
        callbackURL: '/verify-email',
      });
      
      if (error) {
        toast.error(error.message || 'Failed to send verification email');
      } else {
        toast.success('Verification email sent! Check your inbox.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send verification email');
    } finally {
      setIsResendingVerification(false);
    }
  };

  const isLoading = userLoading || prefsLoading;

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load settings"
        message={error instanceof Error ? error.message : 'An unexpected error occurred'}
        onRetry={handleRefresh}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="User Settings"
        description="Manage your profile and preferences"
        onRefresh={handleRefresh}
        isRefreshing={isFetching}
      />

      {/* Statistics Cards */}
      <StatsGrid columns={3}>
        <StatCard
          label="Theme"
          value={theme.charAt(0).toUpperCase() + theme.slice(1)}
          icon={Settings}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Timezone"
          value={timezone.split('/').pop() || timezone}
          icon={User}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          label="Page Size"
          value={tablePageSize}
          icon={Settings}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
      </StatsGrid>

      {/* Profile Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
        </div>
        
        <div className="px-6 py-6 space-y-6">
          {/* Email Verification Warning */}
          {currentUser?.user && !currentUser.user.emailVerified && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">
                    Your email address has not been verified
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Please verify your email address to ensure you can receive important notifications.
                  </p>
                  <button
                    onClick={handleResendVerification}
                    disabled={isResendingVerification}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    {isResendingVerification ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Your name"
            />
            <p className="mt-1 text-sm text-gray-500">
              This name will be displayed throughout the application
            </p>
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{currentUser?.user?.email}</span>
              </div>
              {currentUser?.user?.emailVerified ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3" />
                  Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <AlertTriangle className="w-3 h-3" />
                  Pending
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Email address cannot be changed
            </p>
          </div>
        </div>

        {/* Profile Action Buttons */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end rounded-b-lg">
          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={!hasProfileChanges || updateProfile.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateProfile.isPending ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>

      {/* Preferences Form */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>
        </div>
        
        <div className="px-6 py-6 space-y-6">
          {/* Theme Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Theme
            </label>
            <div className="flex space-x-4">
              {(['light', 'dark', 'system'] as const).map((option) => (
                <label
                  key={option}
                  className={`flex items-center px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                    theme === option
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="theme"
                    value={option}
                    checked={theme === option}
                    onChange={(e) => setTheme(e.target.value as Theme)}
                    className="sr-only"
                  />
                  <span className="capitalize">{option}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Choose your preferred color scheme
            </p>
          </div>

          {/* Timezone Selection */}
          <div>
            <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Used for displaying timestamps throughout the application
            </p>
          </div>

          {/* Table Page Size */}
          <div>
            <label htmlFor="pageSize" className="block text-sm font-medium text-gray-700 mb-2">
              Table Page Size
            </label>
            <select
              id="pageSize"
              value={tablePageSize}
              onChange={(e) => setTablePageSize(Number(e.target.value))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} items per page
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Default number of items shown in data tables
            </p>
          </div>

          {/* Default View Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default View Mode
            </label>
            <div className="flex space-x-4">
              {([
                { value: 'list', label: 'List', icon: List },
                { value: 'grid', label: 'Grid', icon: Grid },
              ] as const).map(({ value, label, icon: Icon }) => (
                <label
                  key={value}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                    defaultViewMode === value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="viewMode"
                    value={value}
                    checked={defaultViewMode === value}
                    onChange={(e) => setDefaultViewMode(e.target.value as ViewMode)}
                    className="sr-only"
                  />
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Choose between list (table) or grid (cards) view for data pages
            </p>
          </div>

          {/* Sidebar Collapsed */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="sidebarCollapsed" className="block text-sm font-medium text-gray-700">
                  Collapse Sidebar by Default
                </label>
                <p className="text-sm text-gray-500">
                  Start with a collapsed navigation sidebar
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={sidebarCollapsed}
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  sidebarCollapsed ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    sidebarCollapsed ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Preferences Action Buttons */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3 rounded-b-lg">
          <button
            type="button"
            onClick={handleResetPreferences}
            disabled={!hasPreferencesChanges}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSavePreferences}
            disabled={!hasPreferencesChanges || updatePreferences.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updatePreferences.isPending ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserSettingsPage;
